<template>
  <div>
    <!-- 1. TOOLBAR SUPERIOR PLANO -->
    <v-toolbar flat color="white" class="border-bottom flex-grow-0" height="60">
      <v-btn icon @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>

      <v-toolbar-title class="font-weight-bold grey--text text--darken-4 text-subtitle-1 d-flex align-center">
        <v-icon left :color="isView ? 'blue darken-2' : (isEdit ? 'amber darken-3' : 'green darken-2')">
          {{ isView ? 'mdi-eye-outline' : (isEdit ? 'mdi-pencil-box-outline' : 'mdi-plus-box-outline') }}
        </v-icon>
        {{ isView ? 'Visualizar Programación Comercial' : (isEdit ? 'Editar Programación Comercial' : 'Nueva Programación Comercial') }}
      </v-toolbar-title>

      <v-chip v-if="formData.EcpID" small class="ml-3 font-weight-bold" :color="getStatusColor(formData.EcpStatus)">
        {{ formData.StatusName || 'Borrador' }}
      </v-chip>

      <!-- Indicador de Cambios -->
      <div v-if="!isView" class="ml-3 d-none d-sm-flex align-center">
        <v-chip
          x-small
          :color="hasUnsavedChanges ? 'amber lighten-4' : 'green lighten-5'"
          :text-color="hasUnsavedChanges ? 'amber darken-4' : 'green darken-4'"
          class="font-weight-bold"
        >
          <v-icon left x-small>{{ hasUnsavedChanges ? 'mdi-circle-medium' : 'mdi-check-circle-outline' }}</v-icon>
          {{ hasUnsavedChanges ? 'Cambios sin guardar' : 'Guardado' }}
        </v-chip>
      </div>

      <v-spacer />

      <!-- Botones de Acción Superiores -->
      <div class="d-flex align-center">
        <v-btn
          color="grey darken-2"
          outlined
          small
          class="text-capitalize mr-2 rounded-lg font-weight-bold"
          @click="$emit('open-capacity-config')"
        >
          <v-icon left small>mdi-cog-box</v-icon>
          Capacidades
        </v-btn>

        <!-- Activar si está en Borrador -->
        <v-btn
          v-if="formData.EcpID > 0 && formData.EcpStatus === 1"
          color="green darken-2"
          outlined
          small
          class="text-capitalize mr-2 rounded-lg font-weight-bold"
          @click="$emit('activate')"
        >
          <v-icon left small>mdi-check-decagram-outline</v-icon>
          Activar
        </v-btn>

        <!-- Cerrar si está Activo -->
        <v-btn
          v-if="formData.EcpID > 0 && formData.EcpStatus === 2"
          color="grey darken-3"
          outlined
          small
          class="text-capitalize mr-2 rounded-lg font-weight-bold"
          @click="$emit('close-prog')"
        >
          <v-icon left small>mdi-lock-outline</v-icon>
          Cerrar
        </v-btn>

        <v-btn
          v-if="!isView"
          color="grey darken-2"
          outlined
          small
          class="text-capitalize mr-2 rounded-lg font-weight-bold"
          @click="$emit('paste-excel')"
          :disabled="!formData.EmbID"
        >
          <v-icon left small>mdi-clipboard-text-outline</v-icon>
          Pegar desde Excel
        </v-btn>

        <v-btn
          v-if="!isView"
          color="primary"
          small
          class="text-capitalize rounded-lg px-4 font-weight-bold"
          @click="$emit('save')"
          :loading="saving"
          :disabled="!formData.EmbID"
        >
          <v-icon left small>mdi-content-save-outline</v-icon>
          Guardar Programación
        </v-btn>
      </div>
    </v-toolbar>

    <!-- 2. CAMPOS PRINCIPALES DE CABECERA -->
    <div class="pa-3 bg-white border-bottom">
      <v-row dense align="center">
        <!-- Selector Presupuesto Maestro -->
        <v-col cols="12" md="3">
          <s-select
            v-model="formData.EmbID"
            :items="lockedBudgets"
            item-text="text"
            item-value="value"
            label="Presupuesto Maestro Bloqueado *"
            clearable
            dense
            hide-details
            :disabled="isEdit || isView || (formData.EcpID > 0)"
            @input="$emit('budget-selected', $event)"
          />
        </v-col>

        <!-- Nombre de Programación -->
        <v-col cols="12" md="3">
          <s-text
            label="Nombre de la Programación *"
            v-model="formData.EcpName"
            placeholder="ej: Programa Comercial Frescos 2026..."
            :disabled="isView"
            :maxlength="150"
            dense
            hide-details
            @input="$emit('mark-dirty')"
          />
        </v-col>

        <!-- Fecha -->
        <v-col cols="6" md="2">
          <s-date
            label="Fecha Programación"
            v-model="formData.EcpDate"
            :disabled="isView"
            dense
            hide-details
            @input="$emit('mark-dirty')"
          />
        </v-col>

        <!-- Moneda -->
        <v-col cols="6" md="1">
          <s-select-definition
            v-model="formData.TypeCurrency"
            :def="1129"
            label="Moneda"
            :disabled="true"
            dense
            hide-details
          />
        </v-col>

        <!-- Observación General -->
        <v-col cols="12" md="3">
          <s-text
            label="Observación General"
            v-model="formData.EcpObservation"
            placeholder="Notas generales de la programación..."
            :disabled="isView"
            :maxlength="300"
            dense
            hide-details
            @input="$emit('mark-dirty')"
          />
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
export default {
  name: "CommercialProgramHeader",
  props: {
    formData: {
      type: Object,
      required: true,
    },
    lockedBudgets: {
      type: Array,
      default: () => [],
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    isView: {
      type: Boolean,
      default: false,
    },
    saving: {
      type: Boolean,
      default: false,
    },
    hasUnsavedChanges: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    getStatusColor(status) {
      switch (status) {
        case 1: return "blue lighten-4 blue--text text--darken-3";
        case 2: return "green lighten-4 green--text text--darken-3";
        case 3: return "grey lighten-3 grey--text text--darken-3";
        case 4: return "red lighten-4 red--text text--darken-3";
        default: return "grey lighten-3";
      }
    },
  },
};
</script>

<style scoped>
.border-bottom {
  border-bottom: 1px solid #e2e8f0 !important;
}
.gap-2 {
  gap: 8px;
}
</style>
